import type { CurrencyConfig, CurrencyCode } from '@/config/countries/types'

interface FormatOptions {
  compact?: boolean           // Use compact notation (1.2Cr, 500K)
  showSymbol?: boolean        // Show currency symbol
  showCode?: boolean          // Show currency code (USD, INR)
}

const defaultOptions: FormatOptions = {
  compact: false,
  showSymbol: true,
  showCode: false,
}

// Currency symbols map
const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: '$',
  AED: 'د.إ',
  INR: '₹',
}

// Currency locales map
const CURRENCY_LOCALES: Record<CurrencyCode, string> = {
  USD: 'en-US',
  AED: 'en-AE',
  INR: 'en-IN',
}

/**
 * Format a number as currency using the provided config
 */
export function formatCurrency(
  amount: number,
  config: CurrencyConfig,
  options: FormatOptions = {}
): string {
  const opts = { ...defaultOptions, ...options }

  if (opts.compact) {
    return formatCompact(amount, config)
  }

  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: opts.showSymbol ? 'currency' : 'decimal',
      currency: config.code,
      maximumFractionDigits: config.decimalPlaces,
      minimumFractionDigits: 0,
    })

    let result = formatter.format(amount)

    if (opts.showCode && !opts.showSymbol) {
      result = `${result} ${config.code}`
    }

    return result
  } catch {
    // Fallback for unsupported locales
    return `${config.symbol}${amount.toLocaleString()}`
  }
}

/**
 * Format as compact notation (1.2Cr, 500K, etc.)
 */
function formatCompact(amount: number, config: CurrencyConfig): string {
  const absAmount = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''

  // Indian numbering system for INR
  if (config.code === 'INR') {
    if (absAmount >= 10000000) {
      return `${sign}${config.symbol}${(absAmount / 10000000).toFixed(1)}Cr`
    }
    if (absAmount >= 100000) {
      return `${sign}${config.symbol}${(absAmount / 100000).toFixed(1)}L`
    }
    if (absAmount >= 1000) {
      return `${sign}${config.symbol}${(absAmount / 1000).toFixed(0)}K`
    }
    return `${sign}${config.symbol}${absAmount.toFixed(0)}`
  }

  // Western numbering system
  if (absAmount >= 1000000000) {
    return `${sign}${config.symbol}${(absAmount / 1000000000).toFixed(1)}B`
  }
  if (absAmount >= 1000000) {
    return `${sign}${config.symbol}${(absAmount / 1000000).toFixed(1)}M`
  }
  if (absAmount >= 1000) {
    return `${sign}${config.symbol}${(absAmount / 1000).toFixed(0)}K`
  }
  return `${sign}${config.symbol}${absAmount.toFixed(0)}`
}

/**
 * Parse a currency string back to a number
 */
export function parseCurrency(value: string): number {
  // Remove currency symbols, commas, spaces
  const cleaned = value.replace(/[^0-9.-]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format currency by currency code (simple helper)
 */
export function formatByCurrencyCode(amount: number, currencyCode: CurrencyCode, compact = false): string {
  const symbol = CURRENCY_SYMBOLS[currencyCode]
  const absAmount = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''

  if (compact) {
    // Indian numbering system for INR
    if (currencyCode === 'INR') {
      if (absAmount >= 10000000) {
        return `${sign}${symbol}${(absAmount / 10000000).toFixed(1)}Cr`
      }
      if (absAmount >= 100000) {
        return `${sign}${symbol}${(absAmount / 100000).toFixed(1)}L`
      }
      if (absAmount >= 1000) {
        return `${sign}${symbol}${(absAmount / 1000).toFixed(0)}K`
      }
      return `${sign}${symbol}${absAmount.toFixed(0)}`
    }

    // Western numbering system (USD, AED)
    if (absAmount >= 1000000000) {
      return `${sign}${symbol}${(absAmount / 1000000000).toFixed(1)}B`
    }
    if (absAmount >= 1000000) {
      return `${sign}${symbol}${(absAmount / 1000000).toFixed(1)}M`
    }
    if (absAmount >= 1000) {
      return `${sign}${symbol}${(absAmount / 1000).toFixed(0)}K`
    }
    return `${sign}${symbol}${absAmount.toFixed(0)}`
  }

  // Full format
  try {
    const locale = CURRENCY_LOCALES[currencyCode]
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(amount)
  } catch {
    return `${symbol}${amount.toLocaleString()}`
  }
}

/**
 * Get currency symbol by code
 */
export function getCurrencySymbol(currencyCode: CurrencyCode): string {
  return CURRENCY_SYMBOLS[currencyCode]
}

/**
 * Format with explicit country display (e.g., "AED 100K" or "$100K")
 */
export function formatCompactWithSymbol(amount: number, currencyCode: CurrencyCode): string {
  return formatByCurrencyCode(amount, currencyCode, true)
}
